import { useState } from 'react'
import { Trash2 } from 'lucide-react'
import { AddTile, Popover, PopoverContent, PopoverTrigger } from '@teach/ui'
import { cn } from '@/lib/utils'
import { GradientCustomBuilder } from './GradientCustomDialog'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  value: string | null
  onSelect: (g: string) => void
  presets: string[]
  custom: string[]
  onRemoveCustom: (g: string) => void
  onSaveCustom: (g: string) => void
  disabled: boolean
}

export const GradientPresets = ({
  value,
  onSelect,
  presets,
  custom,
  onRemoveCustom,
  onSaveCustom,
  disabled,
}: Props) => {
  const [open, setOpen] = useState(false)
  const m = useMessages(messages)
  return (
    <div className="grid max-w-md grid-cols-7 gap-2">
      {presets.map((g) => (
        <button
          key={g}
          type="button"
          onClick={() => onSelect(g)}
          disabled={disabled}
          className={cn(
            'aspect-square rounded-xl transition-all hover:scale-105 disabled:pointer-events-none disabled:opacity-50',
            value === g ? 'ring-2 ring-foreground ring-offset-2 ring-offset-background' : null,
          )}
          style={{ background: g }}
          aria-label={m.pickGradient}
        />
      ))}

      {custom.map((g) => (
        <div key={g} className="relative group">
          <button
            type="button"
            onClick={() => onSelect(g)}
            disabled={disabled}
            className={cn(
              'w-full aspect-square rounded-xl transition-all hover:scale-105 disabled:pointer-events-none disabled:opacity-50',
              value === g ? 'ring-2 ring-foreground ring-offset-2 ring-offset-background' : null,
            )}
            style={{ background: g }}
            aria-label={m.pickGradient}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onRemoveCustom(g)
            }}
            aria-label={m.removeGradient}
            disabled={disabled}
            className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow-sm hover:bg-destructive/90 opacity-0 group-hover:opacity-100 transition-all z-10 disabled:hidden"
          >
            <Trash2 className="w-2.5 h-2.5" />
          </button>
        </div>
      ))}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <AddTile className="aspect-square" disabled={disabled} />
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3" align="end">
          <GradientCustomBuilder
            onChange={onSelect}
            onSave={(g) => {
              onSaveCustom(g)
              setOpen(false)
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
